export const APP_SELECTOR_PATH = '/';
export const AUTH_COMPLETE_PATH = '/auth/complete';
export const SIGNED_OUT_PATH = '/auth/signed-out';
export const DEV_DOCS_ACCESS_ROLE = 'sportsfi-dev-docs-access';

export type SportsFiApp = 'admin' | 'club' | 'player' | 'finance' | 'gpe';
export type SportsFiOrganisationType = 'CLUB' | 'PLAYER' | 'FINANCIER' | 'GENERAL_PRIVATE_ENTITY';

export type SportsFiUserOrganisation = {
  organisationId: string;
  type: SportsFiOrganisationType;
  name: string;
  club?: {
    imageUrl?: string | null;
    officialName?: string | null;
  } | null;
  player?: {
    imageUrl?: string | null;
    shortName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  isDefault?: boolean;
};

export type AccountSecurityStatusForAccess = {
  hasTotp: boolean;
  hasPasskey: boolean;
  recoveryCodesConfigured: boolean;
};

export type AccountSecuritySetupRequirement = 'mfa' | 'recovery_codes';
export type AccountSecurityRequiredAction = 'CONFIGURE_TOTP' | 'CONFIGURE_RECOVERY_AUTHN_CODES';

export type LocationLike = {
  protocol: string;
  hostname: string;
  host?: string;
};

export type SportsFiAppConfig = {
  app: SportsFiApp;
  hostPrefix: string;
  label: string;
  role: string;
  defaultPath: string;
  organisationTypes: readonly SportsFiOrganisationType[];
};

export const SPORTSFI_APP_CONFIG: Record<SportsFiApp, SportsFiAppConfig> = {
  admin: {
    app: 'admin',
    hostPrefix: 'admin',
    label: 'Admin',
    role: 'admin-app-access',
    defaultPath: '/manage',
    organisationTypes: [],
  },
  club: {
    app: 'club',
    hostPrefix: 'club',
    label: 'Club',
    role: 'club-app-access',
    defaultPath: '/capital-hub/finance-desk',
    organisationTypes: ['CLUB'],
  },
  player: {
    app: 'player',
    hostPrefix: 'player',
    label: 'Player',
    role: 'club-app-access',
    defaultPath: '/team/contracts',
    organisationTypes: ['PLAYER'],
  },
  finance: {
    app: 'finance',
    hostPrefix: 'finance',
    label: 'Finance',
    role: 'finance-app-access',
    defaultPath: '/finance-desk/overview',
    organisationTypes: ['FINANCIER'],
  },
  gpe: {
    app: 'gpe',
    hostPrefix: 'gpe',
    label: 'GPE',
    role: 'gpe-app-access',
    defaultPath: '/gpe-finance-desk',
    organisationTypes: ['GENERAL_PRIVATE_ENTITY'],
  },
};

export const SPORTSFI_APPS = Object.keys(SPORTSFI_APP_CONFIG) as SportsFiApp[];

export const PROTECTED_APP_ROLES = SPORTSFI_APPS.map((app) => SPORTSFI_APP_CONFIG[app].role).filter(
  (role, index, roles) => roles.indexOf(role) === index
);

const APP_BY_HOST_PREFIX = SPORTSFI_APPS.reduce<Record<string, SportsFiApp>>((accumulator, app) => {
  accumulator[SPORTSFI_APP_CONFIG[app].hostPrefix] = app;

  return accumulator;
}, {});

const HOST_SUFFIXES = [
  { suffix: 'local.sportsfi.tech', protocol: 'https:' },
  { suffix: 'dev.sportsfi.tech', protocol: 'https:' },
  { suffix: 'stg.sportsfi.tech', protocol: 'https:' },
  { suffix: 'sportsfi.tech', protocol: 'https:' },
  { suffix: 'sportsfi.com', protocol: 'https:' },
] as const;

function stripPort(hostname: string): string {
  if (hostname.startsWith('[')) {
    return hostname.replace(/^\[(.*)](?::\d+)?$/, '$1').toLowerCase();
  }

  return hostname.replace(/:\d+$/, '').toLowerCase();
}

function normalisePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function getCurrentOrigin(currentLocation?: LocationLike): string {
  if (!currentLocation) {
    return 'https://club.local.sportsfi.tech';
  }

  const host = currentLocation.host ?? currentLocation.hostname;

  return `${currentLocation.protocol}//${host}`;
}

export function getSportsFiHostContext(hostname: string): {
  prefix: string | null;
  suffix: string;
  protocol: string;
} | null {
  const normalisedHostname = stripPort(hostname);

  for (const hostSuffix of HOST_SUFFIXES) {
    if (normalisedHostname === hostSuffix.suffix) {
      return { prefix: null, suffix: hostSuffix.suffix, protocol: hostSuffix.protocol };
    }

    const suffixWithDot = `.${hostSuffix.suffix}`;

    if (normalisedHostname.endsWith(suffixWithDot)) {
      const subdomain = normalisedHostname.slice(0, -suffixWithDot.length);
      const [prefix] = subdomain.split('.');

      return { prefix: prefix ?? null, suffix: hostSuffix.suffix, protocol: hostSuffix.protocol };
    }
  }

  return null;
}

export function getAppFromHostname(hostname: string): SportsFiApp | null {
  const prefix = getSportsFiHostContext(hostname)?.prefix;

  return prefix ? (APP_BY_HOST_PREFIX[prefix] ?? null) : null;
}

export function getAppConfigForHostname(hostname: string): SportsFiAppConfig | null {
  const app = getAppFromHostname(hostname);

  return app ? SPORTSFI_APP_CONFIG[app] : null;
}

function buildSportsFiSubdomainUrl(hostPrefix: string, path: string, currentLocation?: LocationLike): string {
  const safePath = normalisePath(path);
  const currentHostname = currentLocation?.hostname ? stripPort(currentLocation.hostname) : null;

  if (currentHostname === 'localhost' || currentHostname === '127.0.0.1' || currentHostname === '::1') {
    return new URL(safePath, getCurrentOrigin(currentLocation)).toString();
  }

  const context = currentHostname ? getSportsFiHostContext(currentHostname) : null;
  const suffix = context?.suffix ?? 'local.sportsfi.tech';
  const protocol = context?.protocol ?? 'https:';

  return `${protocol}//${hostPrefix}.${suffix}${safePath}`;
}

const DEV_DOCS_ENABLED_SUFFIXES = new Set(['local.sportsfi.tech', 'stg.sportsfi.tech']);

export function buildSportsFiAppUrl(app: SportsFiApp, path: string, currentLocation?: LocationLike): string {
  return buildSportsFiSubdomainUrl(SPORTSFI_APP_CONFIG[app].hostPrefix, path, currentLocation);
}

export function buildSportsFiOnboardingUrl(path: string, currentLocation?: LocationLike): string {
  return buildSportsFiSubdomainUrl('onboarding', path, currentLocation);
}

export function buildSportsFiDevDocsUrl(currentLocation?: LocationLike): string | null {
  const currentHostname = currentLocation?.hostname ? stripPort(currentLocation.hostname) : null;

  if (!currentHostname) {
    return null;
  }

  if (currentHostname === 'localhost' || currentHostname === '127.0.0.1' || currentHostname === '::1') {
    return 'https://docs.local.sportsfi.tech';
  }

  const context = getSportsFiHostContext(currentHostname);

  if (!context || !DEV_DOCS_ENABLED_SUFFIXES.has(context.suffix)) {
    return null;
  }

  return `${context.protocol}//docs.${context.suffix}`;
}

export function buildSportsFiAppSelectorUrl({
  currentLocation,
  returnUrl,
}: {
  currentLocation?: LocationLike;
  returnUrl?: string | null;
} = {}): string {
  const selectorUrl = new URL(APP_SELECTOR_PATH, buildSportsFiOnboardingUrl('/', currentLocation));

  if (returnUrl) {
    selectorUrl.searchParams.set('returnUrl', returnUrl);
  }

  return selectorUrl.toString();
}

export function isSportsFiAppSelectorUrl(url: URL): boolean {
  return url.pathname === APP_SELECTOR_PATH && getSportsFiHostContext(url.hostname)?.prefix === 'onboarding';
}

export function getTrustedSportsFiUrl(rawUrl: string | null | undefined, currentLocation?: LocationLike): URL | null {
  if (!rawUrl) {
    return null;
  }

  try {
    const url = new URL(rawUrl, getCurrentOrigin(currentLocation));
    const hostname = stripPort(url.hostname);
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }

    if (!isLocalhost && getSportsFiHostContext(hostname) == null) {
      return null;
    }

    return url;
  } catch {
    return null;
  }
}

export function hasAccessToApp(userRoles: readonly string[] | undefined, app: SportsFiApp): boolean {
  return userRoles?.includes(SPORTSFI_APP_CONFIG[app].role) === true;
}

export function hasProtectedAppRole(userRoles: readonly string[] | undefined): boolean {
  return userRoles?.some((role) => PROTECTED_APP_ROLES.includes(role)) === true;
}

export function hasAccessToDevDocs(userRoles: readonly string[] | undefined): boolean {
  return userRoles?.includes(DEV_DOCS_ACCESS_ROLE) === true;
}

export function getAccountSecuritySetupRequirement(
  securityStatus: AccountSecurityStatusForAccess | null | undefined,
  userRoles: readonly string[] | undefined
): AccountSecuritySetupRequirement | null {
  if (!hasProtectedAppRole(userRoles) || securityStatus == null) {
    return null;
  }

  if (!securityStatus.hasTotp && !securityStatus.hasPasskey) {
    return 'mfa';
  }

  if (!securityStatus.recoveryCodesConfigured) {
    return 'recovery_codes';
  }

  return null;
}

export function getAccountSecuritySetupRequiredAction(
  requirement: AccountSecuritySetupRequirement
): AccountSecurityRequiredAction {
  return requirement === 'recovery_codes' ? 'CONFIGURE_RECOVERY_AUTHN_CODES' : 'CONFIGURE_TOTP';
}

export function buildAccountSecuritySetupCompletionUrl({
  currentLocation,
  requiredAction,
  returnUrl,
}: {
  currentLocation?: LocationLike;
  requiredAction: AccountSecurityRequiredAction;
  returnUrl?: string | null;
}): string {
  const completionUrl = new URL(AUTH_COMPLETE_PATH, getCurrentOrigin(currentLocation));
  const trustedReturnUrl = getTrustedSportsFiUrl(returnUrl, currentLocation);

  if (trustedReturnUrl != null) {
    completionUrl.searchParams.set('returnUrl', trustedReturnUrl.toString());
  }

  completionUrl.searchParams.set('accountSecuritySetup', 'required');
  completionUrl.searchParams.set('accountSecurityAction', requiredAction);
  completionUrl.searchParams.set('accountSecurityActionAttempted', 'true');

  return completionUrl.toString();
}

export function hasAttemptedAccountSecuritySetupAction({
  accountSecurityAction,
  accountSecurityActionAttempted,
  accountSecuritySetup,
  requiredAction,
}: {
  accountSecurityAction?: string | null;
  accountSecurityActionAttempted?: string | null;
  accountSecuritySetup?: string | null;
  requiredAction: AccountSecurityRequiredAction;
}): boolean {
  return (
    accountSecuritySetup === 'required' &&
    accountSecurityActionAttempted === 'true' &&
    accountSecurityAction === requiredAction
  );
}

export function getAccountSecuritySetupPath(app: SportsFiApp): string {
  switch (app) {
    case 'admin':
      return '/admin-profile/security';
    case 'finance':
      return '/funder-profile/security';
    case 'gpe':
      return '/gpe-profile/security';
    case 'club':
    case 'player':
      return '/profile/security';
  }
}

export function isAccountSecuritySetupPath(app: SportsFiApp, pathname: string): boolean {
  const setupPath = getAccountSecuritySetupPath(app);

  return pathname === setupPath || pathname.startsWith(`${setupPath}/`);
}

function getFirstProtectedAppForRoles(userRoles: readonly string[] | undefined): SportsFiApp | null {
  for (const app of SPORTSFI_APPS) {
    if (hasAccessToApp(userRoles, app)) {
      return app;
    }
  }

  return null;
}

export function getPreferredAccountSecuritySetupApp({
  returnUrl,
  currentApp,
  userRoles,
  organisations,
  currentLocation,
}: {
  returnUrl?: string | null;
  currentApp?: SportsFiApp | null;
  userRoles?: readonly string[];
  organisations: readonly SportsFiUserOrganisation[];
  currentLocation?: LocationLike;
}): SportsFiApp | null {
  const trustedReturnUrl = getTrustedSportsFiUrl(returnUrl, currentLocation);
  const targetApp = trustedReturnUrl == null ? null : getAppFromHostname(trustedReturnUrl.hostname);

  if (targetApp != null && hasAccessToApp(userRoles, targetApp)) {
    return targetApp;
  }

  if (currentApp != null && hasAccessToApp(userRoles, currentApp)) {
    return currentApp;
  }

  const firstAccessibleOrganisationOption = getAccessibleOrganisationOptions(organisations, userRoles)[0];

  return firstAccessibleOrganisationOption?.app ?? getFirstProtectedAppForRoles(userRoles);
}

export function buildAccountSecuritySetupUrl({
  app,
  currentLocation,
  returnUrl,
}: {
  app: SportsFiApp;
  currentLocation?: LocationLike;
  returnUrl?: string | null;
}): string {
  const setupUrl = new URL(buildSportsFiAppUrl(app, getAccountSecuritySetupPath(app), currentLocation));
  const trustedReturnUrl = getTrustedSportsFiUrl(returnUrl, currentLocation);

  if (trustedReturnUrl != null && !isAccountSecuritySetupPath(app, trustedReturnUrl.pathname)) {
    setupUrl.searchParams.set('returnUrl', trustedReturnUrl.toString());
  }

  return setupUrl.toString();
}

export function getAppForOrganisationType(type: SportsFiOrganisationType): SportsFiApp {
  switch (type) {
    case 'CLUB':
      return 'club';
    case 'PLAYER':
      return 'player';
    case 'FINANCIER':
      return 'finance';
    case 'GENERAL_PRIVATE_ENTITY':
      return 'gpe';
  }
}

export function getOrganisationsForApp(
  organisations: readonly SportsFiUserOrganisation[],
  app: SportsFiApp
): SportsFiUserOrganisation[] {
  const allowedTypes = SPORTSFI_APP_CONFIG[app].organisationTypes;

  if (allowedTypes.length === 0) {
    return [];
  }

  return organisations.filter((organisation) => allowedTypes.includes(organisation.type));
}

function getDefaultOrganisationForApp(
  organisations: readonly SportsFiUserOrganisation[],
  app: SportsFiApp
): SportsFiUserOrganisation | null {
  return getOrganisationsForApp(organisations, app).find((organisation) => organisation.isDefault === true) ?? null;
}

export type AccessibleOrganisationOption = {
  app: SportsFiApp;
  config: SportsFiAppConfig;
  organisation: SportsFiUserOrganisation;
};

export function getAccessibleOrganisationOptions(
  organisations: readonly SportsFiUserOrganisation[],
  _userRoles: readonly string[] | undefined
): AccessibleOrganisationOption[] {
  return organisations.map((organisation) => {
    const app = getAppForOrganisationType(organisation.type);
    const config = SPORTSFI_APP_CONFIG[app];

    return { app, config, organisation };
  });
}

export type AuthCompletionDecision =
  | {
      kind: 'app';
      app: SportsFiApp;
      url: string;
      organisationId?: string;
    }
  | {
      kind: 'selector';
      url: string;
    };

export type AppSelectorDecision =
  | {
      kind: 'show-selector';
    }
  | Extract<AuthCompletionDecision, { kind: 'app' }>;

export type CurrentAppAccessDecision =
  | {
      kind: 'allow';
    }
  | {
      kind: 'selector';
      url: string;
    }
  | {
      kind: 'switch-organisation';
      organisationId: string;
      url: string;
    }
  | {
      kind: 'account-security-setup';
      kcAction: AccountSecurityRequiredAction;
      callbackUrl: string;
    };

function buildSelectorUrl(currentLocation?: LocationLike, returnUrl?: string): string {
  return buildSportsFiAppSelectorUrl({ currentLocation, returnUrl });
}

function getTrustedReturnUrl(rawUrl: string | null | undefined, currentLocation?: LocationLike): string | undefined {
  return getTrustedSportsFiUrl(rawUrl, currentLocation)?.toString();
}

export function resolveAuthCompletionDecision({
  returnUrl,
  userRoles,
  organisations,
  currentLocation,
}: {
  returnUrl?: string | null;
  userRoles?: readonly string[];
  organisations: readonly SportsFiUserOrganisation[];
  currentLocation?: LocationLike;
}): AuthCompletionDecision {
  const trustedReturnUrl = getTrustedSportsFiUrl(returnUrl, currentLocation);

  if (!trustedReturnUrl) {
    return { kind: 'selector', url: buildSelectorUrl(currentLocation) };
  }

  const targetApp = getAppFromHostname(trustedReturnUrl.hostname);

  if (isSportsFiAppSelectorUrl(trustedReturnUrl) || trustedReturnUrl.pathname === '/unauthorized') {
    return { kind: 'selector', url: buildSelectorUrl(currentLocation) };
  }

  if (!targetApp) {
    return { kind: 'selector', url: buildSelectorUrl(currentLocation) };
  }

  const returnUrlString = trustedReturnUrl.toString();

  if (targetApp === 'admin') {
    if (!hasAccessToApp(userRoles, targetApp)) {
      return { kind: 'selector', url: buildSelectorUrl(currentLocation) };
    }

    return { kind: 'app', app: targetApp, url: returnUrlString };
  }

  const matchingOrganisations = getOrganisationsForApp(organisations, targetApp);

  if (matchingOrganisations.length === 0) {
    return { kind: 'selector', url: buildSelectorUrl(currentLocation) };
  }

  if (matchingOrganisations.length === 1) {
    return {
      kind: 'app',
      app: targetApp,
      url: returnUrlString,
      organisationId: matchingOrganisations[0]?.organisationId,
    };
  }

  const defaultOrganisation = getDefaultOrganisationForApp(organisations, targetApp);

  if (defaultOrganisation != null) {
    return {
      kind: 'app',
      app: targetApp,
      url: returnUrlString,
      organisationId: defaultOrganisation.organisationId,
    };
  }

  return {
    kind: 'selector',
    url: buildSelectorUrl(currentLocation, returnUrlString),
  };
}

export function resolveAppSelectorDecision({
  returnUrl: _returnUrl,
  userRoles: _userRoles,
  organisations: _organisations,
  currentLocation: _currentLocation,
}: {
  returnUrl?: string | null;
  userRoles?: readonly string[];
  organisations: readonly SportsFiUserOrganisation[];
  currentLocation?: LocationLike;
}): AppSelectorDecision {
  return { kind: 'show-selector' };
}

export function resolveCurrentAppAccessDecision({
  app,
  currentUrl,
  userRoles,
  organisations,
  activeOrganisation,
  selectedOrganisationId,
  currentLocation,
}: {
  app: SportsFiApp | null;
  currentUrl?: string | null;
  userRoles?: readonly string[];
  organisations: readonly SportsFiUserOrganisation[];
  activeOrganisation?: Pick<SportsFiUserOrganisation, 'organisationId' | 'type'> | null;
  selectedOrganisationId?: string | null;
  currentLocation?: LocationLike;
}): CurrentAppAccessDecision {
  if (app == null) {
    return { kind: 'allow' };
  }

  const trustedCurrentUrl = getTrustedReturnUrl(currentUrl, currentLocation);

  if (app === 'admin') {
    return hasAccessToApp(userRoles, app)
      ? { kind: 'allow' }
      : { kind: 'selector', url: buildSelectorUrl(currentLocation, trustedCurrentUrl) };
  }

  const selectedOrganisation =
    selectedOrganisationId == null
      ? null
      : organisations.find((organisation) => organisation.organisationId === selectedOrganisationId);

  if (selectedOrganisation != null && getAppForOrganisationType(selectedOrganisation.type) === app) {
    return { kind: 'allow' };
  }

  if (activeOrganisation != null && getAppForOrganisationType(activeOrganisation.type) === app) {
    return { kind: 'allow' };
  }

  return { kind: 'selector', url: buildSelectorUrl(currentLocation, trustedCurrentUrl) };
}
